import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
  Ban,
  CheckCircle2,
  ClipboardCheck,
  FileSignature,
  FileText,
  Building2,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Quote as QuoteIcon,
} from "lucide-react"
import { useEffect, useState, type JSX } from "react"

import { Pagination } from "@/components/ui/pagination"
import { resellersApiService, type ResellerTimelineEntry } from "@crm-src/lib/resellers-api"

/** kind → glyph + palette colour. A kind this build does not know about falls
 * back to a neutral document icon rather than crashing or rendering nothing —
 * crm-api can ship a new event type before the SPA does. */
const ENTRY_STYLE: Record<string, { Icon: typeof FileText; color: string }> = {
  company_created: { Icon: Building2, color: "primary" },
  application_submitted: { Icon: ClipboardCheck, color: "info" },
  documents_signed: { Icon: FileSignature, color: "info" },
  countersigned: { Icon: ShieldCheck, color: "success" },
  application_decided: { Icon: CheckCircle2, color: "success" },
  member_invited: { Icon: UserPlus, color: "info" },
  member_revoked: { Icon: UserMinus, color: "warning" },
  account_activated: { Icon: CheckCircle2, color: "success" },
  account_deactivated: { Icon: Ban, color: "error" },
  quote_issued: { Icon: QuoteIcon, color: "primary" },
  quote_decided: { Icon: QuoteIcon, color: "success" },
  file_uploaded: { Icon: FileText, color: "info" },
}

function styleFor(kind: string): { Icon: typeof FileText; color: string } {
  return ENTRY_STYLE[kind] ?? { Icon: FileText, color: "primary" }
}

function formatWhen(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/**
 * Activity — what has happened with this reseller company.
 *
 * DERIVED server-side from rows that already exist (registration, signatures,
 * seats, the account switch, quotes, files), not from an audit table. The
 * consequence worth knowing at this end: entries a source cannot timestamp
 * honestly are simply absent — customers and prospects are not here, because
 * nothing records WHEN a company took one on. The Network tab lists those.
 *
 * Quote entries are omitted entirely for a viewer without `crm:financials`;
 * that is enforced in crm-api, not here, so this component needs no wall of
 * its own.
 */
export function ResellerActivityTab({ resellerId }: { resellerId: string }): JSX.Element {
  const [entries, setEntries] = useState<ResellerTimelineEntry[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total_count: 0, total_pages: 1, per_page: 25 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    resellersApiService
      .getTimeline(resellerId, { page, per_page: 25 })
      .then((result) => {
        if (cancelled) return
        setEntries(result.entries)
        setPagination(result.pagination)
      })
      .catch(() => {
        if (cancelled) return
        setEntries([])
        setError("Couldn't load this reseller's history.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [resellerId, page])

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-cy="reseller-activity-tab">
      <CardContent>
        {error ? (
          <Box sx={{ textAlign: "center", color: "error.main", py: 4 }}>
            <Typography sx={{ fontSize: 14 }}>{error}</Typography>
          </Box>
        ) : entries.length === 0 ? (
          <Box sx={{ textAlign: "center", color: "text.secondary", py: 6, bgcolor: "surfaceSoft", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 14 }}>Nothing has happened with this reseller yet.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {entries.map((entry, index) => {
              const { Icon, color } = styleFor(entry.kind)
              const isLast = index === entries.length - 1

              return (
                <Box key={`${entry.kind}-${entry.at}-${index}`} sx={{ display: "flex", gap: 2 }}>
                  {/* The rail: a dot per event, joined by a line that stops at
                      the last one so the timeline does not trail into nothing. */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: (t) => alpha((t.palette as never as Record<string, { main: string }>)[color]?.main ?? t.palette.primary.main, 0.12),
                        color: `${color}.main`,
                      }}
                    >
                      <Icon size={16} />
                    </Box>
                    {!isLast && <Box sx={{ width: "2px", flex: 1, bgcolor: "divider", my: 0.5 }} />}
                  </Box>

                  <Box sx={{ pb: isLast ? 0 : 3, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{entry.title}</Typography>
                    {entry.detail && (
                      <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>{entry.detail}</Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.5 }}>
                      {formatWhen(entry.at)}
                      {entry.actor ? ` · ${entry.actor}` : ""}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}

        <Pagination
          page={page}
          perPage={pagination.per_page}
          totalCount={pagination.total_count}
          totalPages={pagination.total_pages}
          onPageChange={setPage}
          entityNoun="events"
          hideIfSinglePage
        />
      </CardContent>
    </Card>
  )
}
